import { ApolloError, gql, useApolloClient, useQuery } from '@apollo/client';
import { useCallback } from 'react';
import { useParams } from 'react-router-dom';

import { Server } from './apollo/types';

export type ClientRequiresTelehealthSafetyGuardsQuery = {
  clientRequiresTelehealthSafetyGuards: boolean;
};

export type ClientRequiresTelehealthSafetyGuardsQueryVariables = {
  patientShortId: string;
};

export const GET_CLIENT_SAFETY_GUARD = gql`
  query ClientRequiresTelehealthSafetyGuards($patientShortId: String!) {
    clientRequiresTelehealthSafetyGuards(patientShortId: $patientShortId)
  }
`;

function computeResult(
  data: ClientRequiresTelehealthSafetyGuardsQuery | null | undefined,
  loading: boolean | undefined,
  error: ApolloError | undefined,
) {
  if (loading && !data) {
    return undefined;
  } else if (error) {
    // If the query fails, default to true to err on the side of caution for
    // clients whose safety guard status is unknown.
    return true;
  } else {
    return data?.clientRequiresTelehealthSafetyGuards ?? true;
  }
}

/**
 * Wrapper for useQuery to get the client's safety guard status. Safety guards
 * are legally required for clients under 13 years old for several experiences
 * in Telehealth, such as filling out forms and using chat. Query is public and invocable
 * by either the client (visitor) or the provider. For the provider, it is
 * necessary to pass the patient short ID as a parameter to the hook.
 *
 * @param passedPatientShortId - Optional patient short ID to pass to the hook.
 * Required if used in a provider context.
 * @returns QueryResult and callback function for the result of the query.
 */
export function useGetClientSafetyGuard(passedPatientShortId?: string) {
  const { patientShortId: patientShortIdParam } = useParams();
  const patientShortId = passedPatientShortId ?? patientShortIdParam;
  const apolloClient = useApolloClient();

  const { data, error, loading, ...rest } = useQuery<ClientRequiresTelehealthSafetyGuardsQuery>(
    GET_CLIENT_SAFETY_GUARD,
    {
      variables: { patientShortId },
      context: { server: Server.MONOLITH, skipAuth: true },
      skip: !patientShortId,
    },
  );

  // Memoized callback to read the cached result from the Apollo cache at run
  // time. Returns boolean value or undefined if no data is available in the
  // Apollo cache (i.e. the query has not yet completed/errored out).
  const getRequiresSafetyGuards = useCallback(() => {
    if (!patientShortId) return undefined;
    const data = apolloClient.readQuery<ClientRequiresTelehealthSafetyGuardsQuery>({
      query: GET_CLIENT_SAFETY_GUARD,
      variables: { patientShortId },
    });

    return data?.clientRequiresTelehealthSafetyGuards;
  }, [apolloClient, patientShortId]);

  return {
    loading,
    error,
    getRequiresSafetyGuards,
    requiresSafetyGuards: computeResult(data, loading, error),
    ...rest,
  };
}
